import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Container from "../Common/Container";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 2rem;
  margin: 3rem 0;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 1.5rem;
    margin: 2rem 0;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 1rem;
    margin: 1.5rem 0;
  }
`;

const categories = [
  {
    name: "Resin Jewelry",
    image: "/category1.PNG",
    link: "/shop?category=Resin Jewelry",
  },
  {
    name: "Pet Tags",
    image: "/category2.PNG",
    link: "/shop?category=Pet Tags",
  },
  {
    name: "Embroidery",
    image: "/category3.PNG",
    link: "/shop?category=Embroidery",
  },
  {
    name: "Accessories",
    image: "/category4.PNG",
    link: "/shop?category=Accessories",
  },
];

const Card = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #f4efef;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(233, 125, 125, 0.07);
  text-decoration: none;
  color: #e97d7dff;
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s, border 0.2s;
  border: 2px solid transparent;

  &:hover {
    box-shadow: 0 4px 24px rgba(233, 125, 125, 0.13);
    transform: translateY(-4px) scale(1.03);
    border-color: #e97d7dff;
  }

  @media (max-width: 768px) {
    border-radius: 0.8rem;

    &:hover {
      transform: translateY(-2px) scale(1.02);
    }
  }

  @media (max-width: 480px) {
    border-radius: 0.6rem;

    &:hover {
      transform: translateY(-1px) scale(1.01);
    }
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 140px;
  object-fit: cover;

  @media (max-width: 768px) {
    height: 120px;
  }

  @media (max-width: 480px) {
    height: 100px;
  }
`;

const CardTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  padding: 1rem 0.5rem;
  text-align: center;
  color: #e97d7dff;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.8rem 0.4rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    padding: 0.6rem 0.3rem;
  }
`;

const AnimatedCard = styled.div<{ isVisible: boolean; delay: number }>`
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transform: ${(props) =>
    props.isVisible ? "translateY(0) scale(1)" : "translateY(50px) scale(0.8)"};
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transition-delay: ${(props) => props.delay}ms;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2rem;
  margin-bottom: 1.5rem;
  animation: float 3s ease-in-out infinite;
  position: relative;

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-5px);
    }
  }
`;

const ShopByCategory: React.FC = () => {
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollAnimation({
    threshold: 0.1,
    rootMargin: "0px 0px -50px 0px",
    triggerOnce: false,
  });

  // Reset animation state when component mounts
  useEffect(() => {
    setShouldAnimate(false);
    // Trigger animation after component mounts
    setTimeout(() => {
      setShouldAnimate(true);
    }, 200);
  }, []);

  return (
    <section ref={sectionRef}>
      <Container>
        <SectionTitle>Shop by Category</SectionTitle>
        <Grid>
          {categories.map((cat, index) => (
            <AnimatedCard
              key={cat.name}
              isVisible={shouldAnimate}
              delay={shouldAnimate ? index * 100 : 0}
            >
              <Card href={cat.link}>
                <CardImage src={cat.image} alt={cat.name} />
                <CardTitle>{cat.name}</CardTitle>
              </Card>
            </AnimatedCard>
          ))}
        </Grid>
      </Container>
    </section>
  );
};

export default ShopByCategory;
